import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import CheckboxTree from '../../components/connectedForm/CheckboxTree';
import styled from 'styled-components';
import * as QueryHelper from '../../utils/QueryHelper';
import { IListWithEdges } from '../../utils/QueryHelper';
import { useDispatch } from 'react-redux';
import {
  IProduct,
  IProductCategoriesQueryResult,
  IProductCategoriesQueryVariables,
  IProductCategory,
  IProductProductCategory,
} from '../../graphql/schema/p4types';
import HeaderStatus from './HeaderStatus';
import { default as Form } from '../../components/connectedForm';
import { DocumentNode } from 'graphql';
import { uuIdGenerator } from '../../utils/UuIdGenerator';

const Div = styled.div``;

const initialCheckboxTree = ({ categories }: { categories: Array<IProductProductCategory> }) => {
  if (typeof categories === 'undefined' || categories === null) {
    return [];
  }
  const checked: Array<string> = [];
  categories.forEach((values) => {
    if (!values.category!.deleted) {
      checked.push(`${values.category!.id}`);
    }
  });

  return checked;
};

/**
 * Wrapper class for dropdown to handle data querying.
 */
const ProductCategoryCheckboxTree = ({
  change,
  values,
  query,
}: {
  change: (
    field: string,
    value: any,
    mutationId?: string
  ) => {
    type: string;
    meta: { form: string; field: string; touch: boolean; mutationId?: string };
    payload: any;
  };
  values: IProduct;
  query: DocumentNode;
}) => {
  const dispatch = useDispatch();
  const [checked, setChecked] = useState<Array<string>>([]);
  const [expanded, setExpanded] = useState<Array<any>>([]);

  useEffect(() => {
    setChecked(initialCheckboxTree({ categories: values.categories as Array<IProductProductCategory> }));
  }, [values.categories, values.categories?.length]);

  const constructNodes = (data: Array<IProductCategory>, root = null) => {
    const rootNodes: Array<{ value: string; label: string; children: Array<any> }> = [];
    data.forEach((values) => {
      if (values.parent === root || (values.parent && values.parent.id === root)) {
        rootNodes.push({
          value: values.id.toString(),
          label: values.name ?? 'missing',
          children: constructNodes(data, values.id),
        });
      }
    });
    return rootNodes;
  };

  const onCheck = (checkedArray: Array<string>) => {
    const newProductRecord = checkedArray.map((value) => {
      return {
        category: {
          id: parseInt(value, 10),
        },
        product: {
          id: values.id,
        },
        sortOrder: 0,
      };
    });

    const productRecord = [
      ...newProductRecord.filter(
        (newProduct) => !values.categories!.some((product) => product?.category?.id === newProduct.category.id)
      ),
      ...values
        .categories!.map((value) => {
          if (checkedArray.includes(value?.category?.id.toString())) {
            return {
              id: value?.id,
              uuid: uuIdGenerator(),
              category: {
                id: value?.category?.id,
              },
              product: {
                id: values.id,
              },
            };
          }
        })
        .filter((notUndefined) => notUndefined),
    ];

    dispatch(change('categories', productRecord));
    setChecked(checkedArray);
  };

  const onExpand = (expanded: Array<any>) => {
    setExpanded(expanded);
  };

  const { data, fetchMore } = useQuery<IProductCategoriesQueryResult, IProductCategoriesQueryVariables>(query, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    variables: {
      first: 100,
      orderBy: 'sortOrder',
    },
  });

  useEffect(() => {
    if (data?.list?.pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          orderBy: 'sortOrder',
          after: data?.list?.edges?.length,
        },
      });
    }
  }, [data?.list?.edges?.length, data?.list?.pageInfo.endCursor, data?.list?.pageInfo.hasNextPage, fetchMore]);

  if (!data?.list || data.list?.edges?.length === 0) {
    return <Div>{'No categories found'}</Div>;
  }

  const labelData = QueryHelper.processData(data as IListWithEdges);

  const categoryLabels = labelData.map((value) => {
    if (checked.includes(value.id.toString())) {
      return (
        <HeaderStatus key={value.id} statusStyle={'info'} title={value.name} tooltip={value.breadcrumb.join(' / ')} />
      );
    }
  });

  // build the tree here
  const nodes = constructNodes(QueryHelper.processData(data as IListWithEdges));
  return (
    <>
      <Form.Fieldset cols={1}>{categoryLabels}</Form.Fieldset>
      <CheckboxTree
        nodes={nodes}
        checked={checked}
        expanded={expanded}
        onCheck={onCheck}
        onExpand={onExpand}
        noCascade={true}
        showExpandAll={true}
      />
    </>
  );
};

export default ProductCategoryCheckboxTree;
